import { ALL_POD_PRODUCTS as POD_PRODUCTS, podIntentFor, assertResolvedSpec } from './pod-products.js';
import { GENERATED_POD_SPECS, AVAILABLE_PRODUCT_PHOTOS } from './generated-pod-specs.js';
import { TEMPORARY_PRODUCT_PHOTOS, TEMPORARY_PRODUCTS, temporaryProductFor } from './temporary-product-photos.js';

// Manufacturing fields are never derived from concept photographs or CMS edits.
export const PRODUCT_DESIGNS = Object.fromEntries([
  ...POD_PRODUCTS.map(cfg => [cfg.slug, {
    productType: cfg.productType, garmentColor: cfg.color.toLowerCase(),
    productColor: cfg.color.toLowerCase(), view: cfg.view,
    mark: `/brand/${cfg.file.replace('.png', '.svg')}`,
    placement: cfg.layout, mascot: cfg.mascot, printTechnique: cfg.technique,
    podPlacementIntent: cfg.placement,
    source: cfg.mascot === 'scorpion' ? 'rinne-boot-exact' : 'project-source',
    photo: cfg.realPhoto || null,
  }]),
  ...TEMPORARY_PRODUCTS.map(cfg => [cfg.slug, {
    productType: cfg.productType, garmentColor: cfg.color, productColor: cfg.color,
    view: cfg.view, mark: `/brand/${cfg.file.replace('.png', '.svg')}`,
    placement: cfg.layout, mascot: cfg.mascot, printTechnique: null,
    podPlacementIntent: null, source: 'approved-concept', photo: null,
  }]),
]);
const available = new Set([
  ...AVAILABLE_PRODUCT_PHOTOS,
  ...Object.values(TEMPORARY_PRODUCT_PHOTOS).map(p => p.photo),
]);

// Shared priority: verified official > approved temporary concept > explicit
// reference photograph > eligible legacy pseudo > labelled brand placeholder.
export function photoCandidatesFor(product, specs = GENERATED_POD_SPECS, files = available) {
  const slug = product?.slug;
  const cfg = podIntentFor(slug) || temporaryProductFor(slug);
  const spec = specs[slug];
  const candidates = [];
  const add = (photo, photoSource) => {
    if (photo && files.has(photo) && !candidates.some(c => c.photo === photo)) candidates.push({ photo, photoSource });
  };
  try {
    assertResolvedSpec(spec);
    if (spec.mockup?.source === 'printful' && spec.mockup.variantId === cfg.variantId && spec.mockup.placement === cfg.placement && spec.mockup.view === cfg.view) add(spec.mockup.path, 'printful');
  } catch { /* Missing or stale production records are never called official. */ }
  const concept = TEMPORARY_PRODUCT_PHOTOS[slug];
  if (concept?.approvedForDisplay && concept.view === cfg?.view) add(concept.photo, 'concept');
  const metadata = product?.metadata?.design;
  if (metadata?.photoSource === 'real' && (cfg?.view !== 'back' || metadata.photoView === 'back')) add(metadata.photo, 'real');
  add(cfg?.realPhoto, 'real');
  // The legacy back composites show a front-facing model and stay excluded.
  if (cfg && cfg.view !== 'back') add(`/products/pseudo/${cfg.slug}.jpg`, 'pseudo');
  candidates.push({ photo: cfg ? `/brand/${cfg.file.replace('.png', '.svg')}` : '/brand/scorpion-main.svg', photoSource: 'fallback' });
  return candidates;
}
export function photoSourceLabel(source, view) {
  if (source === 'printful') return `PRINTFUL · ${view === 'back' ? 'BACK' : view === 'front' ? 'FRONT' : 'PRINT SURFACE'}`;
  if (source === 'concept') return 'AI MOCKUP · 暫定イメージ';
  if (source === 'real') return 'REFERENCE PHOTO · 製造仕様未検証';
  if (source === 'pseudo') return 'DESIGN STUDY · 正式商品画像ではありません';
  return '商品画像未取得 · ブランド原画（非モックアップ）';
}
export function designFor(product) {
  const base = PRODUCT_DESIGNS[product?.slug] || {
    productType: product?.category || 'tee', garmentColor: 'white', view: 'front',
    mark: '/brand/scorpion-main.svg', placement: null, mascot: 'scorpion',
    printTechnique: null, podPlacementIntent: null, source: 'fallback',
  };
  const candidates = photoCandidatesFor(product);
  const selected = candidates[0];
  return { ...base, ...selected, photoCandidates: candidates,
    photoLabel: photoSourceLabel(selected.photoSource, base.view),
    podSpec: GENERATED_POD_SPECS[product?.slug] || null };
}
